import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { instrumentationSchema } from './shared/instrumentation-schema';
import { getAssociatedCampaignIds, getGoal, readAssociatedCampaigns } from './conversion-goals-repository';
import { GoalCreationWizard } from './goal-creation-wizard';
import { STEP_PATHS } from './step-paths';
import { getObjectiveByGoalType } from './shared/config';
import { startActivity } from './shared/util';

const GoalCreationPageUnstyled = (props) => {
  const {
    mockData,
    onWizardExit,
    permissions,
    serverContext,
    scope,
    perfMarker,
    instrumentation,
    registerBackButtonClickedHandler,
    isDemo,
    hasConversionCouponFunc,
    onCouponAdoptedFunc,
    autoGoalBackHandlerFunc,
    autoGoalSelectedObjective,
  } = props;

  const { goalId } = scope.values;
  const {
    CurrentAccount: {
      AdvertiserCustomerID: cid,
      Id: aid,
    },
  } = serverContext;
  const GTMContext = useMemo(() => ({
    AllowTagCloning: true,
    IsGTMCloningChecked: true,
  }), []);
  const [goalData, setGoalData] = useState({});
  const [wizardDefaultState, setWizardDefaultState] = useState({});
  useMemo(() => instrumentation.addScenario(instrumentationSchema), [instrumentation]);

  const isLinkedInEnabled = useMemo(() =>
    permissions && permissions.dynamic && permissions.dynamic.LinkedInCampaigns, [permissions]);

  const { logScenario, logActivity } = useMemo(() => {
    const scenario = instrumentation.goalCreationWizard.create();
    const logInfo = {
      logScenario: scenario,
      logActivity: scenario.createWizard.create(),
    };
    startActivity(logInfo.logActivity);
    return logInfo;
  }, [instrumentation.goalCreationWizard]);

  const getWizardStates = useCallback((goalObj, associatedCampaigns = []) => {
    if (autoGoalBackHandlerFunc) {
      const updatedState = {
        currentStep: autoGoalSelectedObjective ? STEP_PATHS.goalType.id : STEP_PATHS.goalObjective.id,
        steps: [],
        loggingSteps: [],
        isEditScenario: false,
        startFromEdit: false,
        logScenario,
        GTMContext,
        associatedCampaigns: {
          initial: associatedCampaigns,
          current: associatedCampaigns,
        },
        permissions,
      };
      if (autoGoalSelectedObjective) {
        updatedState.selectedObjective = autoGoalSelectedObjective;
      }
      return updatedState;
    }

    if (_.isEmpty(goalObj)) {
      return {
        currentStep: STEP_PATHS.goalObjective.id,
        steps: [],
        loggingSteps: [],
        isEditScenario: false,
        startFromEdit: false,
        tagInstallOption: 'no',
        logScenario,
        GTMContext,
        associatedCampaigns: {
          initial: associatedCampaigns,
          current: associatedCampaigns,
        },
        permissions,
      };
    } else if (goalObj.Type === 'SmartGoal') {
      return {
        currentStep: STEP_PATHS.goalDetails.smartGoal.id,
        steps: [STEP_PATHS.goalObjective.id, STEP_PATHS.goalType.id],
        loggingSteps: [STEP_PATHS.goalObjective.id, STEP_PATHS.goalType.id],
        goalObject: { ...goalObj },
        isEditScenario: true,
        tagInstallOption: 'no',
        startFromEdit: true,
        logScenario,
        GTMContext,
        associatedCampaigns: {
          initial: associatedCampaigns,
          current: associatedCampaigns,
        },
        permissions,
      };
    }
    const goalObjective = getObjectiveByGoalType(goalObj.Type);
    return {
      currentStep: STEP_PATHS.goalType.id,
      steps: [STEP_PATHS.goalObjective.id],
      loggingSteps: [STEP_PATHS.goalObjective.id],
      selectedObjective: goalObjective,
      tagInstallOption: 'no',
      goalObject: { ...goalObj },
      isEditScenario: true,
      startFromEdit: true,
      logScenario,
      GTMContext,
      associatedCampaigns: {
        initial: associatedCampaigns,
        current: associatedCampaigns,
      },
      permissions,
    };
  }, [GTMContext, autoGoalBackHandlerFunc, autoGoalSelectedObjective, logScenario, permissions]);

  useEffect(() => {
    if (goalId) {
      logActivity.trace(`Editing goal id ${goalId}`);
      if (_.isEmpty(mockData)) {
        getGoal(goalId, cid, aid).toPromise()
          .then((goalResponse) => {
            const hasAssociatedCampaigns = !_.isEmpty(getAssociatedCampaignIds(goalResponse));

            if (isLinkedInEnabled && hasAssociatedCampaigns) {
              readAssociatedCampaigns(cid, aid, goalResponse)
                .then((campaignsResponse) => {
                  const associatedCampaigns = campaignsResponse.value;

                  setGoalData(goalResponse);
                  setWizardDefaultState(getWizardStates(goalResponse, associatedCampaigns));
                });
            } else {
              setGoalData(goalResponse);
              setWizardDefaultState(getWizardStates(goalResponse));
            }
          });
      } else {
        setGoalData(mockData);
        setWizardDefaultState(getWizardStates(mockData));
      }
    } else {
      logActivity.trace('Creating new goal');
      setWizardDefaultState(getWizardStates({}));
    }
  }, [aid, cid, getWizardStates, logActivity, goalId, mockData, isLinkedInEnabled]);

  return (
    !_.isEmpty(wizardDefaultState)) && (
      <GoalCreationWizard
        onWizardExit={onWizardExit}
        permissions={permissions}
        serverContext={serverContext}
        scope={scope}
        perfMarker={perfMarker}
        goalData={goalData}
        isDemo={isDemo}
        wizardDefaultState={wizardDefaultState}
        parentActivity={logActivity}
        registerBackButtonClickedHandler={registerBackButtonClickedHandler}
        hasConversionCouponFunc={hasConversionCouponFunc}
        onCouponAdoptedFunc={onCouponAdoptedFunc}
        autoGoalBackHandlerFunc={autoGoalBackHandlerFunc}
        autoGoalSelectedObjective={autoGoalSelectedObjective}
      />
  );
};

GoalCreationPageUnstyled.propTypes = {
  mockData: PropTypes.shape({}),
  onWizardExit: PropTypes.func.isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      LinkedInCampaigns: PropTypes.bool,
    }),
  }).isRequired,
  serverContext: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({}),
  }).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  registerBackButtonClickedHandler: PropTypes.func.isRequired,
  isDemo: PropTypes.bool,
  hasConversionCouponFunc: PropTypes.func.isRequired,
  onCouponAdoptedFunc: PropTypes.func.isRequired,
  autoGoalBackHandlerFunc: PropTypes.func,
  autoGoalSelectedObjective: PropTypes.string,
};

GoalCreationPageUnstyled.defaultProps = {
  mockData: {},
  isDemo: false,
  autoGoalBackHandlerFunc: null,
  autoGoalSelectedObjective: null,
};


export const GoalCreationPage = withInstrumentation(GoalCreationPageUnstyled);
